import XBadge from "@/components/XBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast.js";
import { CirclePlus } from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useCreateCollaborators, useGetProjects } from "../hooks/projects";
import * as projectsService from "../service/projects";
import useProjectStore from "../store/projects";
const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
};

const CollaboratorsDialog = () => {
  const { toast } = useToast();
  const {
    modals,
    setModalOpen,
    closeModal,
    currentProject: project,
  } = useProjectStore();
  const [collaborators, setCollaborators] = useState([]);
  const [currentCollaborator, setCurrentCollaborator] = useState("");
  const [error, setError] = useState("");
  const { refreshProjects } = useGetProjects();

  const { createCollaborators } = useCreateCollaborators();

  const handleAddCollaborator = () => {
    if (!validateEmail(currentCollaborator)) {
      setError("Please enter a valid email address");
      return;
    }

    if (!collaborators.includes(currentCollaborator)) {
      setCollaborators([...collaborators, currentCollaborator]);
      setCurrentCollaborator("");
      setError("");
    }
  };

  const handleRemoveCollaborator = (collaboratorToRemove) => {
    setCollaborators(collaborators.filter((c) => c !== collaboratorToRemove));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddCollaborator();
    }
  };

  const handleInputChange = (event) => {
    setCurrentCollaborator(event.target.value);
    setError("");
  };

  const handleSave = () => {
    createCollaborators(project.id, collaborators)
      .then(() => {
        toast({
          title: "Success!",
          description: "Collaborators added successfully.",
          variant: "success",
        });
        handleClose();
      })
      .catch(() => {
        toast({
          title: "Error!",
          description:
            "There was an error adding collaborators. Please try again.",
          variant: "destructive",
        });
      });
  };
  const handleClose = () => {
    refreshProjects();
    closeModal("collaborators");
  };
  useEffect(() => {
    if (project?.id) {
      projectsService.getCollaborators(project.id).then((data) => {
        const emails = (data || [])
          .filter((c) => c.role !== "OWNER")
          .map((c) => c.email);
        setCollaborators(emails);
      });
    }

    return () => {
      setCollaborators([]);
    };
  }, [project]);

  return (
    <Dialog
      open={modals.collaborators}
      onOpenChange={(value) => setModalOpen("collaborators", value)}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Collaborators</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <Label htmlFor="tags">Current collaborators</Label>
          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-3 flex flex-wrap gap-2">
              {collaborators?.length === 0
                ? "-"
                : collaborators.map((c) => (
                    <XBadge
                      key={c}
                      label={c}
                      handleRemove={handleRemoveCollaborator}
                    />
                  ))}
            </div>
          </div>
          <Label htmlFor="new-tag">Email</Label>
          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-3 flex flex-col gap-2">
              <Input
                id="new-tag"
                value={currentCollaborator}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Enter email address..."
                className="col-span-3"
                type="email"
              />
              {error && <span className="text-sm text-red-500">{error}</span>}
            </div>
            <Button
              variant="borderless"
              className="font-semibold"
              onClick={handleAddCollaborator}
            >
              <CirclePlus className="size-8" />
              Add
            </Button>
          </div>
        </div>
        <div className="flex justify-between">
          <Button variant="secondary" onClick={handleClose}>
            Back
          </Button>
          <Button type="submit" onClick={handleSave}>
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

CollaboratorsDialog.propTypes = {
  project: PropTypes.object,
};

export default CollaboratorsDialog;
